import React, { useState } from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./multi-step-form.module.scss";
import { validatePassword } from "../../../customer-util";
import PasswordRequirementsBox from "../password-requirement-box";
import { EyeOpenedIcon, EyeClosedIcon } from "../../../../../assets/icons";
import { FormData } from "./types";

interface Props {
	control: Control<FormData>;
	errors: FieldErrors<FormData>;
	password: string;
	isLoading: boolean;
	onNext: () => void;
}

export const Step3Password: React.FC<Props> = ({ control, errors, password, isLoading, onNext }) => {
	const { t } = useTranslation();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	return (
		<div className={styles.stepContent}>
			<div className={styles.card}>
				<div>
					<div className={styles.cardHeader}>
						<span className={styles.cardLabel}>{t("common.labels.password")}</span>
					</div>
					<Controller
						name="password"
						control={control}
						rules={{
							required: t("corporate.passwordRequired"),
							minLength: { value: 10, message: t("auth.atLeastTenCharacters") },
							validate: validatePassword,
						}}
						render={({ field }) => (
							<div className={styles.passwordField}>
								<input
									{...field}
									type={showPassword ? "text" : "password"}
									placeholder={t("auth.web.createYourPassword")}
									className={`${styles.cardInput} ${errors.password ? styles.inputError : ""}`}
								/>
								<button type="button" className={styles.eyeButton} onClick={() => setShowPassword(v => !v)}>
									{showPassword ? <EyeOpenedIcon /> : <EyeClosedIcon />}
								</button>
								{errors.password && <span className={styles.fieldError}>{errors.password.message}</span>}
							</div>
						)}
					/>
				</div>
				<div>
					<div className={styles.cardHeader}>
						<span className={styles.cardLabel}>{t("common.labels.confirmPassword")}</span>
					</div>
					<Controller
						name="confirmPassword"
						control={control}
						rules={{
							required: t("auth.web.pleaseConfirmPassword"),
							validate: value => value === password || t("common.validation.passwordsDoNotMatch"),
						}}
						render={({ field }) => (
							<div className={styles.passwordField}>
								<input
									{...field}
									type={showConfirmPassword ? "text" : "password"}
									placeholder={t("auth.web.confirmYourPassword")}
									className={`${styles.cardInput} ${errors.confirmPassword ? styles.inputError : ""}`}
								/>
								<button type="button" className={styles.eyeButton} onClick={() => setShowConfirmPassword(v => !v)}>
									{showConfirmPassword ? <EyeOpenedIcon /> : <EyeClosedIcon />}
								</button>
								{errors.confirmPassword && <span className={styles.fieldError}>{errors.confirmPassword.message}</span>}
							</div>
						)}
					/>
				</div>

				<div className={styles.fieldDivider} />
				<div className={styles.passwordRequirements}>
					<span>{t("common.labels.passwordRequirementsTitle")}</span>
					<span>{t("auth.web.useTenToSixtyFourCharacters")}</span>
					<span>- {t("auth.oneUppercaseLetter")}</span>
					<span>- {t("auth.web.oneLowercaseLetter")}</span>
					<span>- {t("auth.web.oneNumber")}</span>
					<span>- {t("auth.web.oneSpecialCharacterExample")}</span>
					<span></span>
					<span></span>
					<span>{t("auth.web.avoidUsingEmailPart")}</span>
				</div>
			</div>
			
			<div className={styles.buttonSection}>

			<button type="button" className={styles.continueButton} onClick={onNext} disabled={isLoading}>
				{isLoading ? t("auth.web.creatingAccount") : t("common.actions.continueKey")}
			</button>
			</div>
		</div>
	);
};
